<script lang="ts" setup>
import { onMounted, toRefs, watch } from "vue";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import Button from "@/shared/components/Button/Button.vue";
import Captcha from "@/shared/components/Captcha/Captcha.vue";
import Form from "@/shared/components/Form/Form.vue";
import PermissionChecks from "@/shared/components/PermissionChecks/PermissionChecks.vue";
import SelectField from "@/shared/components/SelectField/SelectField.vue";
import TextButton from "@/shared/components/TextButton/TextButton.vue";
import TextField from "@/shared/components/TextField/TextField.vue";
import Title from "@/shared/components/Title/Title.vue";
import Icon from "@/shared/components/Icon/Icon.vue";
import { TEXTFIELD_MAX_LENGTH_DEFAULT } from "@/shared/helper/constants.helper";
import type { MobilityRegistrationFormData } from "@/app/registration-app/user/mobility-registration/@types/RegistrationFormData";
import { useMobilityRegistrationFields } from "./useMobilityRegistrationFields.composable";

const props = defineProps<{
  isLoading?: boolean;
  hasError?: boolean;
  showBackButton?: boolean;
}>();

const { hasError } = toRefs(props);

const emit = defineEmits<{
  (e: "register", fields: MobilityRegistrationFormData): void;
  (e: "open-help-modal"): void;
}>();

const {
  salutationOptions,
  salutation: { value: salutation, errors: salutationErrors },
  firstName: { value: firstName, errors: firstNameErrors },
  lastName: { value: lastName, errors: lastNameErrors },
  email: { value: email, errors: emailErrors },
  customerId: { value: customerId, errors: customerIdErrors },
  conditions: { value: conditions, errors: conditionsErrors },
  privacy: { value: privacy, errors: privacyErrors },
  captcha: { value: captcha },
  handleSubmit,
  resetForm,
} = useMobilityRegistrationFields();

const onSubmit = handleSubmit((fields: MobilityRegistrationFormData) =>
  emit("register", fields),
);

// A captcha can only be submitted once, so in case of an error, we reset it to allow a new submission.
watch(hasError, (newValue) => {
  if (newValue && captcha?.value) {
    captcha.value = "";
  }
});

onMounted(() => resetForm());
</script>

<template>
  <div class="mobility-registration-form">
    <Title
      size="l"
      :text="$t('registration.user.mobility.title')"
    />
    <p>
      {{ $t("registration.user.mobility.text") }}
    </p>
    <Form @submit="onSubmit">
      <template #default>
        <SelectField
          v-model="salutation"
          :options="salutationOptions"
          :errors="salutationErrors"
          :label="$t('shared.address.salutation')"
        />
        <TextField
          v-model="firstName"
          :errors="firstNameErrors"
          :label="$t('shared.address.firstName')"
          :maxlength="TEXTFIELD_MAX_LENGTH_DEFAULT"
          autocomplete="given-name"
        />
        <TextField
          v-model="lastName"
          :errors="lastNameErrors"
          :label="$t('shared.address.lastName')"
          :maxlength="TEXTFIELD_MAX_LENGTH_DEFAULT"
          autocomplete="family-name"
        />
        <TextField
          v-model="email"
          :errors="emailErrors"
          :label="$t('shared.term.email.short')"
          autocomplete="email"
        />
        <TextField
          v-model="customerId"
          :errors="customerIdErrors"
          :label="$t('registration.user.mobility.customerId')"
          inputmode="numeric"
        >
          <template #append>
            <Icon
              class="mobility-registration-form--customer-id-icon"
              icon="help"
              @click="$emit('open-help-modal')"
            />
          </template>
        </TextField>
        <div class="mobility-registration-form--permissions">
          <PermissionChecks
            v-model:conditions="conditions"
            v-model:privacy="privacy"
            :conditions-errors="conditionsErrors"
            :privacy-errors="privacyErrors"
            :has-newsletter-checkbox="false"
          />
        </div>
        <Captcha v-model="captcha" />
      </template>
      <template #actions>
        <div class="mobility-registration-form--actions">
          <Button
            type="submit"
            :caption="$t('registration.shared.action.register')"
            :is-loading="isLoading"
          />
          <Button
            :caption="$t('shared.action.reset.default')"
            :is-loading="isLoading"
            variant="outlined-black"
            @click="resetForm"
          />
        </div>
      </template>
    </Form>
    <TextButton
      v-if="showBackButton"
      :caption="$t('registration.user.backToOverview')"
      icon="arrowBack"
      :to="{ name: AppRouteNames.RegistrationOverview }"
    />
  </div>
</template>

<style scoped>
@import "MobilityRegistrationForm.css";
</style>
